import React from 'react'
import { Link } from 'react-router-dom'
import { SITE_INFO } from '../data/siteData'
import { useVirksomhedsoplysninger } from '../hooks/useCMS'

export function Footer() {
  const currentYear = new Date().getFullYear()
  const { info } = useVirksomhedsoplysninger()
  const currentInfo = info || SITE_INFO

  return (
    <footer className="bg-[#132835] text-[#FAF8F5] pt-16 pb-12 border-t border-[#1b3a4b]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 pb-12 border-b border-white/10">
          {/* Brand and Mission */}
          <div className="space-y-4">
            <Link to="/" className="inline-block focus:outline-none">
              <img
                src={currentInfo.logos?.brandTransparent || SITE_INFO.logos.brandTransparent}
                alt={currentInfo.name || 'Bo Steendahl'}
                className="h-20 sm:h-24 w-auto object-contain"
              />
            </Link>
            <p className="text-sm text-[#FAF8F5]/75 leading-relaxed">
              KROPSTERAPI · COACHING · HYPNOTERAPI
              <br />
              Vi udvikles og forandres som menneske, men den indre kerne består. Jeg hjælper dig med at skabe balance i krop, tanker og sind gennem sammenhængende forløb.
            </p>
            <div className="pt-2 flex items-center gap-3">
              <img
                src={currentInfo.logos?.rabLogo || SITE_INFO.logos.rabLogo}
                alt="RAB Registreret Kropsterapeut"
                className="h-10 w-auto rounded bg-white p-1 object-contain"
              />
              <img
                src={currentInfo.logos?.totumLogo || SITE_INFO.logos.totumLogo}
                alt="TOTUM Kropsterapi"
                className="h-8 w-auto rounded bg-white p-1 object-contain"
              />
            </div>
          </div>

          {/* Core Areas & Services */}
          <div>
            <h4 className="font-serif text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[#7c875a]" />
              <span>Kerneområder</span>
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link
                  to="/kropsterapi"
                  className="text-[#FAF8F5]/80 hover:text-white hover:underline transition-colors"
                >
                  Kropsterapi (Totum)
                </Link>
              </li>
              <li>
                <Link
                  to="/coaching"
                  className="text-[#FAF8F5]/80 hover:text-white hover:underline transition-colors"
                >
                  Coaching (Mindset &amp; NLP)
                </Link>
              </li>
              <li>
                <Link
                  to="/hypnoterapi"
                  className="text-[#FAF8F5]/80 hover:text-white hover:underline transition-colors"
                >
                  Hypnoterapi
                </Link>
              </li>
              <li>
                <Link
                  to="/forloeb"
                  className="text-[#FAF8F5]/80 hover:text-[#7c875a] font-medium hover:underline transition-colors"
                >
                  Forløb — Ikke enkeltbehandlinger
                </Link>
              </li>
              <li>
                <Link
                  to="/video"
                  className="text-[#FAF8F5]/80 hover:text-white hover:underline transition-colors"
                >
                  Video / Lyd-filer
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick links & Insight */}
          <div>
            <h4 className="font-serif text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[#7c875a]" />
              <span>Om &amp; Vilkår</span>
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link
                  to="/om-bo"
                  className="text-[#FAF8F5]/80 hover:text-white hover:underline transition-colors"
                >
                  Om Bo Steendahl
                </Link>
              </li>
              <li>
                <Link
                  to="/mine-9-grundprincipper"
                  className="text-[#FAF8F5]/80 hover:text-white hover:underline transition-colors"
                >
                  Mine 9 grundprincipper
                </Link>
              </li>
              <li>
                <Link
                  to="/klienthistorie"
                  className="text-[#FAF8F5]/80 hover:text-white hover:underline transition-colors"
                >
                  Klienthistorie (Sorgen gav slip)
                </Link>
              </li>
              <li>
                <Link
                  to="/uddannelse"
                  className="text-[#FAF8F5]/80 hover:text-white hover:underline transition-colors"
                >
                  Dokumenter &amp; Uddannelse
                </Link>
              </li>
              <li>
                <Link
                  to="/afbudspolitik"
                  className="text-[#FAF8F5]/80 hover:text-white hover:underline transition-colors"
                >
                  Afbudspolitik
                </Link>
              </li>
              <li>
                <Link
                  to="/handelsbetingelser"
                  className="text-[#FAF8F5]/80 hover:text-white hover:underline transition-colors"
                >
                  Handelsbetingelser
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact and Clinic details */}
          <div>
            <h4 className="font-serif text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[#7c875a]" />
              <span>Kontakt &amp; Klinik</span>
            </h4>
            <div className="space-y-3 text-sm text-[#FAF8F5]/85">
              <p className="font-medium text-white">{currentInfo.name}</p>
              <p className="flex items-start gap-2.5">
                <i className="fa-solid fa-location-dot text-[#7c875a] mt-1 text-xs" aria-hidden="true" />
                <span>
                  {currentInfo.address}<br />
                  {currentInfo.city}
                </span>
              </p>
              <p className="flex items-center gap-2.5">
                <i className="fa-solid fa-phone text-[#7c875a] text-xs" aria-hidden="true" />
                <a
                  href={`tel:${currentInfo.phoneRaw}`}
                  className="hover:text-white hover:underline font-medium whitespace-nowrap"
                >
                  {currentInfo.phone}
                </a>
              </p>
              <p className="flex items-center gap-2.5">
                <i className="fa-solid fa-envelope text-[#7c875a] text-xs" aria-hidden="true" />
                <a
                  href={`mailto:${currentInfo.email}`}
                  className="hover:text-white hover:underline font-medium whitespace-nowrap"
                >
                  {currentInfo.email}
                </a>
              </p>
              <p className="text-xs text-[#FAF8F5]/60 pt-1">
                CVR: {currentInfo.cvr}
              </p>

              {/* Opening Hours list */}
              <div className="pt-2 border-t border-white/10 text-xs text-[#FAF8F5]/70 space-y-1">
                <p className="font-semibold text-white/90">Åbningstider:</p>
                <p>Mandag: 08.00 - 16.00</p>
                <p>Tirsdag: 08.00 - 17.00</p>
                <p>Onsdag - Torsdag: 10.00 - 18.00</p>
                <p>Fredag: 08.00 - 14.00</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-[#FAF8F5]/60 gap-4">
          <p>© {currentYear} {currentInfo.name} · CVR {currentInfo.cvr} · Alle rettigheder forbeholdes.</p>
          <div className="flex items-center gap-6">
            <Link to="/kontakt" className="hover:text-white transition-colors">
              Kontakt Bo
            </Link>
            <Link to="/handelsbetingelser" className="hover:text-white transition-colors">
              Handelsbetingelser
            </Link>
            <Link to="/afbudspolitik" className="hover:text-white transition-colors">
              Afbudspolitik
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
